/**
 * Locking a file while you work on it.
 *
 * WHY LOCKS, IN THIS PRODUCT
 *   CAD files are opaque binaries. Two people cannot edit a SolidWorks part in
 *   parallel and merge the results — there is no merge. So the only honest way
 *   to coordinate is to agree beforehand who is editing what, which is exactly
 *   what a lock records.
 *
 *   That is also what makes folder synchronisation tractable. A general
 *   two-way sync has to reconcile edits made on both sides at once; with
 *   locks, the remote copy of a locked file cannot change under you, so
 *   "pull" and "push" become ordinary, predictable operations rather than a
 *   merge algorithm nobody trusts.
 *
 * DATA DEFINITIONS
 *
 *   A Lock is { id, seq, projectId, path, userId, at, note?, releasedAt?,
 *   releasedBy? }.
 *   Interpretation: a claim by one person on one path, from `at` until
 *   `releasedAt`. Released locks are KEPT rather than deleted, so "who had this
 *   last week and why" is answerable — the same reasoning as the change log.
 *
 *   A LockMap is a Map from path-key to the Lock currently held on it.
 *   Interpretation: what is claimed right now. A path appears at most once;
 *   releasing removes it.
 *
 * WHAT A LOCK DOES AND DOES NOT DO
 *   It refuses writes from anyone else: rename, delete and upload to a locked
 *   path fail for a person who does not hold the lock. It does NOT prevent
 *   reading, downloading, previewing or commenting — a lock is about avoiding
 *   lost work, not about privacy.
 */
import { pathKey, isInside } from './path.js';
import {} from './change.js';
import {} from './user.js';
import { ok, err } from './result.js';
import { byNewestInstant } from './instant.js';
/** The longest a note may be. Long enough for a reason, short enough for a row. */
export const MAX_LOCK_NOTE = 280;
/**
 * Fold the lock log into what is held right now.
 *
 * Like the change log, locks are append-only: a release is a new state on the
 * same row rather than a deletion, so this is a filter rather than a replay.
 * heldLocks : [Lock] -> LockMap
 */
export function heldLocks(locks) {
    const held = new Map();
    for (const lock of [...locks].sort((a, b) => a.seq - b.seq)) {
        const key = `${lock.projectId}|${pathKey(lock.path)}`;
        if (lock.releasedAt)
            held.delete(key);
        else
            held.set(key, lock);
    }
    return held;
}
/** The lock on one path, if any. lockOn : LockMap ProjectId Path -> Lock|undefined */
export const lockOn = (locks, projectId, path) => locks.get(`${projectId}|${pathKey(path)}`);
/**
 * The lock that stands in the way of touching `path`, which may be on the
 * path itself OR on a folder containing it.
 *
 * Interpretation: locking a folder claims everything inside it. That is what
 * someone means when they lock "Gun_Housing_Assembly" before reworking it, and
 * checking only the exact path would let a colleague edit a part inside a
 * folder you had claimed.
 * blockingLock : LockMap ProjectId Path -> Lock|undefined
 */
export function blockingLock(locks, projectId, path) {
    const direct = lockOn(locks, projectId, path);
    if (direct)
        return direct;
    for (const lock of locks.values()) {
        if (lock.projectId !== projectId)
            continue;
        if (isInside(path, lock.path))
            return lock;
    }
    return undefined;
}
/**
 * May this person write to this path?
 *
 * Returns the lock that refuses them, or undefined when they may proceed —
 * which includes the case where they hold the lock themselves.
 * refusedBy : LockMap ProjectId Path UserId -> Lock|undefined
 */
export function refusedBy(locks, projectId, path, who) {
    const lock = blockingLock(locks, projectId, path);
    return lock && lock.userId !== who ? lock : undefined;
}
/**
 * Check a proposed lock against what is already held.
 *
 * Refuses a second lock on a path someone else has claimed, and refuses a lock
 * that would overlap one — locking a folder whose child is already locked, or
 * a file inside an already-locked folder — because two overlapping claims
 * cannot both be honoured and the conflict should surface now, not later.
 * canLock : LockMap ProjectId Path UserId -> Result<true>
 */
export function canLock(locks, projectId, path, who) {
    for (const lock of locks.values()) {
        if (lock.projectId !== projectId)
            continue;
        const overlaps = isInside(path, lock.path) || isInside(lock.path, path);
        if (!overlaps)
            continue;
        if (lock.userId === who) {
            return pathKey(lock.path) === pathKey(path)
                ? err('you already hold this lock')
                : err(`you already hold a lock on "${lock.path}"`);
        }
        return err(`"${lock.path}" is locked by someone else`);
    }
    return ok(true);
}
/** Validate a note. validateNote : String|undefined -> Result<String|undefined> */
export function validateNote(note) {
    if (note === undefined)
        return ok(undefined);
    const trimmed = note.trim();
    if (trimmed.length === 0)
        return ok(undefined);
    if (trimmed.length > MAX_LOCK_NOTE)
        return err(`a note cannot exceed ${MAX_LOCK_NOTE} characters`);
    return ok(trimmed);
}
/**
 * Everything held, newest claim first, optionally narrowed to one project.
 * Used by the "what is locked" list.
 * locksHeld : LockMap ProjectId|undefined -> [Lock]
 */
export function locksHeld(locks, projectId) {
    return [...locks.values()]
        .filter(l => !projectId || l.projectId === projectId)
        .sort((a, b) => byNewestInstant(a.at, b.at));
}
/** Locks held by one person. locksBy : LockMap UserId -> [Lock] */
export const locksBy = (locks, who) => [...locks.values()].filter(l => l.userId === who).sort((a, b) => byNewestInstant(a.at, b.at));
/**
 * The comment that asks for a lock to be released.
 *
 * The request is deliberately an ordinary comment rather than a new kind of
 * notification: it lands in the thread on the file, it @-mentions the holder so
 * they are told, and the conversation about why stays with the file. One
 * mechanism, not two.
 * unlockRequestBody : Lock String -> String
 */
export function unlockRequestBody(lock, holderHandle) {
    const because = lock.note ? ` (locked for: ${lock.note})` : '';
    return `@${holderHandle} could you release the lock on \`${lock.path}\`${because}? I need to make a change.`;
}
//# sourceMappingURL=lock.js.map